import { FC, MutableRefObject } from 'react'
import { Box, Grid } from '@mui/material'
import {
  locale,
  PriceDisplayType,
  usePersistedCurrencyPreference,
  usePlatformConfig,
  usePriceCurrency,
} from '@dxp-module/common'
import clsx from 'clsx'
import FinanceBreakdownSection from '../../../../../form/components/breakdown/FinanceBreakdownSection/FinanceBreakdownSection'
import CurrencySwitch from '../../../../../common/components/currencySwitch/CurrencySwitch'
import { BreakdownBlock } from '../../../../../common/graphql/__generated__/graphql.autogenerated'
import { useSimulationInfoQuery } from '../../../../../miniWidget/react/useSimulationInfoQuery'
import FinanceBreakdownTitle from '../../../../../form/components/breakdown/FinanceBreakdownTitle/FinanceBreakdownTitle'
import { FinanceBreakdownData } from '../../../../../common/model/Breakdown'
import useCashContext from '../../../../../common/hooks/useCashContext'
import financeBreakdownStyles from './financeBreakdown.styles'

type FinanceBreakdownProps = {
  carGroup?: string
  financeBreakdownData: FinanceBreakdownData | BreakdownBlock[]
  targetRef?: MutableRefObject<undefined>
}

const FinanceBreakdown: FC<FinanceBreakdownProps> = ({
  financeBreakdownData,
  targetRef,
  carGroup,
}) => {
  const styles = financeBreakdownStyles()
  const {
    platformConfig: { priceDisplay },
  } = usePlatformConfig()

  const { isCash, setIsCash } = useCashContext()

  const { primary, secondary } = priceDisplay

  useSimulationInfoQuery(setIsCash, () => undefined)
  const { isSecondaryCurrencyDisplay, setIsSecondaryCurrencyDisplay } =
    usePersistedCurrencyPreference()

  const dataTest = isCash
    ? 'calculator:cashBreakdown'
    : 'calculator:financeBreakdown'
  const { isMultiCurrency } = usePriceCurrency()

  const currentCurrency = isSecondaryCurrencyDisplay
    ? secondary?.currency
    : primary?.currency

  const labelBefore =
    priceDisplay[PriceDisplayType.PRIMARY]?.currencyFormatConfig?.currency ??
    locale.getCurrencyLocaleCodeForLabel(primary?.currency)

  const labelAfter =
    priceDisplay[PriceDisplayType.SECONDARY]?.currencyFormatConfig?.currency ??
    locale.getCurrencyLocaleCodeForLabel(secondary?.currency ?? '')

  const renderBreakdownSection = () => {
    if (Array.isArray(financeBreakdownData)) {
      const breakdownBlocks = financeBreakdownData?.find(
        block => block.currency === currentCurrency
      )
      return (
        <Box padding="0 10">
          {breakdownBlocks?.blocks?.map((section, index) => {
            if (section.length === 0) return null
            return (
              <FinanceBreakdownSection
                carGroup={carGroup}
                key={index}
                index={index}
                financeBreakdownSection={section}
                isSecondaryCurrencyDisplay={isSecondaryCurrencyDisplay}
              />
            )
          })}
        </Box>
      )
    }

    const {
      title: { json: breakdown, title },
    } = financeBreakdownData

    return (
      <Box padding="0 10">
        {title && <FinanceBreakdownTitle title={title} />}
        {currentCurrency &&
          breakdown[currentCurrency]?.length &&
          breakdown[currentCurrency].map((section, index) => (
            <FinanceBreakdownSection
              carGroup={carGroup}
              key={index}
              index={index}
              financeBreakdownSection={section}
              targetRef={targetRef}
              isSecondaryCurrencyDisplay={isSecondaryCurrencyDisplay}
            />
          ))}
      </Box>
    )
  }

  return (
    <>
      {isMultiCurrency && (
        <CurrencySwitch
          name="currency-switch"
          checked={isSecondaryCurrencyDisplay}
          onChange={() => setIsSecondaryCurrencyDisplay(value => !value)}
          disableRipple={false}
          data-test="calculator:currency-toggle"
          labelBefore={labelBefore}
          labelAfter={labelAfter}
        />
      )}
      <Grid
        item
        data-test={dataTest}
        className={clsx([styles.wrapper, { [styles.cashWrapper]: isCash }])}
      >
        {renderBreakdownSection()}
      </Grid>
    </>
  )
}

export default FinanceBreakdown
